import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { Button, Modal, Spinner, Container, Row, Col } from 'react-bootstrap'
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Undo,
  Redo,
  Trash2,
  AlertTriangle,
  Keyboard,
} from 'lucide-react'
import { FaSun } from 'react-icons/fa'
import ImageAnnotationComponentV2, {
  ImageAnnotationComponentV2Handle,
} from '../../annotation/ImageAnnotationComponentV2'
import { useCreateAnnotationMutation } from '../../../services/annotationApi'
import { useViewContentQuery } from '../../../services/contentApi'
import {
  selectedBBoxClassIndex,
  setBoundingBoxStatus,
  BBoxStatus,
  setSelectedBoundingBox,
  saveStateToHistory,
  setSelectedContentId,
  selectSelectedContentId,
  clearAllBoundingBoxes,
  clearInitialBoundingBoxes,
  selectBoundingBoxStatuses,
} from '../../../store/slices/annotationSlice'
import { CreateBBoxAnnotationReq } from '../../../types/api/requests'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { RootState } from '../../../store'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import {
  selectEditingPrediction,
  setEditingPrediction,
  selectActiveConfidenceLevel,
  clearEditingPrediction,
} from '../../../store/slices/predictionSlice'
import { useGetTagsQuery } from '../../../services/tagApi'
import { selectConfidenceThresholds } from '../../../store/slices/uiSettingsSlice'
import { predictionApi, useQueryPredictionsQuery } from '../../../services/predictionApi'

import { QueryPredictionRequest } from '../../../types/prediction'
import { useGetModelQuery } from '../../../services/modelsApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { apiTags } from '../../../services/apiTags'
import { useGetContentImageQuery } from '../../../services/contentApi'
import ErrorDialog from '../../modals/ErrorDialog'
import ConfirmDialog from '../../modals/ConfirmDialog'
import SubmitPredictionsModal from '../../modals/SubmitPredictionsModal'
import FixRejectionsInfoModal from '../../modals/FixRejectionsInfoModal'
import KeyboardShortcutsPanel from '../../annotation/KeyboardShortcutsPanel'
import { predictionToUIBoxes } from '../../annotation/utils/boxTransforms'
import { useInitializeBoxes } from '../../annotation/hooks/useInitializeBoxes'
import ReviewScaffold from '../../annotation/review/ReviewScaffold'
import { usePredictionShortcuts } from '../../annotation/hooks/usePredictionShortcuts'
import { usePredictionReview } from './hooks/usePredictionReview'
import PredictionRightPanel from './PredictionRightPanel'
import SnippetList from './SnippetList'
import ClassificationReviewGrid from './ClassificationReviewGrid'
import ContentImageCard from '../../annotation/ContentImageCard'

type PredictionTab = 'inspect' | 'accepted' | 'rejected'

const PredictionReview: React.FC = () => {
  const dispatch = useAppDispatch()
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showNoMoreImagesModal, setShowNoMoreImagesModal] = useState(false)
  const [showImageEditModal, setShowImageEditModal] = useState(false)
  const [showFixRejectionsModal, setShowFixRejectionsModal] = useState(false)
  const [showKeyboardShortcutsPanel, setShowKeyboardShortcutsPanel] = useState(false)
  const [boxToAutoSelect, setBoxToAutoSelect] = useState<AnnotationDataBoundingBox | null>(null)
  const [isModalImageLoaded, setIsModalImageLoaded] = useState(false)

  // Selecting to keep behavior parity with other selectors; not directly used after tab scoping
  const annotationRef = useRef<ImageAnnotationComponentV2Handle>(null)
  const modalAnnotationRef = useRef<ImageAnnotationComponentV2Handle>(null)

  const [createAnnotation] = useCreateAnnotationMutation()

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const editingPrediction = useAppSelector(selectEditingPrediction)
  const selectedContentId = useAppSelector(selectSelectedContentId)

  // Clear prediction/annotation state when project changes
  const prevProjectIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (prevProjectIdRef.current && prevProjectIdRef.current !== selectedProjectId) {
      // Project changed - clear all state
      dispatch(clearEditingPrediction())
      dispatch(clearAllBoundingBoxes())
      dispatch(clearInitialBoundingBoxes())
      dispatch(setSelectedBoundingBox(undefined))
      dispatch(setSelectedContentId(''))
    }
    prevProjectIdRef.current = selectedProjectId
  }, [selectedProjectId, dispatch])

  const selectedPredictions = useAppSelector(
    (state: RootState) => state.prediction.selectedPredictions
  )
  const selectedBBoxClassIdx = useAppSelector(selectedBBoxClassIndex)

  // Sync selectedContentId when editingPrediction changes (like AnnotationsReview)
  // Use a ref to prevent unnecessary updates when contentid hasn't actually changed
  const prevContentIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const newContentId = editingPrediction?.contentid
    if (newContentId !== prevContentIdRef.current) {
      prevContentIdRef.current = newContentId
      if (newContentId) {
        dispatch(setSelectedContentId(newContentId))
      } else {
        dispatch(setSelectedContentId(''))
      }
    }
  }, [editingPrediction?.contentid, dispatch])

  // moved to usePredictionReview

  const [showBBoxes, setShowBBoxes] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [contrast, setContrast] = useState(0)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  // Track which contentId the image was loaded for to prevent race conditions
  const imageLoadedForContentIdRef = useRef<string | undefined>(undefined)

  const [bboxThreshold] = useState(0.01)

  // Status filter for AAL Review (filters both snippets and canvas boxes in modal)
  const [statusFilter, setStatusFilter] = useState<'all' | 'accepted' | 'rejected' | 'untouched'>(
    'all'
  )

  const [showConfirmUnannotated, setShowConfirmUnannotated] = useState(false)
  // Centralized review state and helpers
  const {
    // state
    setSelectedSnippetIndex,
    hoveredBoxIndex,
    setHoveredBoxIndex,
    activeRightTab,
    setActiveRightTab,
    // selectors
    allBoundingBoxes,
    allUntouchedBBoxes,
    acceptedBBoxes,
    rejectedBBoxes,
    // helpers
    resetForNextImage,
    getCurrentSelectedBox,
  } = usePredictionReview()

  // Get bounding box statuses for AAL review status icons
  const bboxStatusesRaw = useAppSelector(selectBoundingBoxStatuses)
  // Convert BBoxStatus enum to string format expected by Canvas
  const bboxStatuses = useMemo(() => {
    const converted: Record<number, 'untouched' | 'accepted' | 'rejected'> = {}
    Object.entries(bboxStatusesRaw).forEach(([key, value]) => {
      const classIndex = Number(key)
      if (value === BBoxStatus.Accepted) {
        converted[classIndex] = 'accepted'
      } else if (value === BBoxStatus.Rejected) {
        converted[classIndex] = 'rejected'
      } else if (value === BBoxStatus.Untouched) {
        converted[classIndex] = 'untouched'
      }
    })
    return converted
  }, [bboxStatusesRaw])

  // Filter bounding boxes based on status filter (for modal canvas)
  const filteredBoundingBoxes = useMemo(() => {
    if (statusFilter === 'all') return allBoundingBoxes
    return allBoundingBoxes.filter((box) => {
      const status = bboxStatusesRaw[box.class_index]
      if (statusFilter === 'accepted') return status === BBoxStatus.Accepted
      if (statusFilter === 'rejected') return status === BBoxStatus.Rejected
      if (statusFilter === 'untouched')
        return status === BBoxStatus.Untouched || status === undefined
      return true
    })
  }, [allBoundingBoxes, statusFilter, bboxStatusesRaw])

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const queryParams: QueryPredictionRequest = useMemo(
    () => ({
      query: {
        filters: [{ key: 'modelid', value: selectedModel?.id || '' }],
        sort_key: 'contentid',
        sort_val: -1,
        operator: 'and',
        page: 0,
        limit: 1000,
      },
      filter_annotated_content: {
        enable: true,
        projectid: selectedProjectId || '',
        datasetid: project?.datasetid || '',
      },
      uncertainty_threshold: bboxThreshold,
      ...(selectedModel?.batch?.metadata_filter?.datasource &&
      selectedModel?.batch?.metadata_filter?.datasource.length > 0
        ? {
            metadata_filter: {
              datasource: selectedModel?.batch?.metadata_filter?.datasource,
            },
          }
        : {}),
    }),
    [
      selectedProjectId,
      project?.datasetid,
      selectedModel?.id,
      bboxThreshold,
      selectedModel?.batch?.metadata_filter?.datasource,
    ]
  )

  const { data: allPredictions } = useQueryPredictionsQuery(queryParams, {
    skip: !selectedModelId,
  })

  const confidenceRanges = useAppSelector(selectConfidenceThresholds)
  const activeConfidenceLevel = useAppSelector(selectActiveConfidenceLevel)

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const { data: contentData, isLoading: isContentDataLoading } = useViewContentQuery(
    {
      ContentID: selectedContentId || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    {
      skip: !selectedContentId,
    }
  )

  // Get preview image URL and load as base64 (always load since preview is always visible)
  const [previewImageCacheTimestamp, setPreviewImageCacheTimestamp] = useState<number>(Date.now())
  const [previewBase64Image, setPreviewBase64Image] = useState<string | null>(null)

  // Reset preview image cache when contentId changes
  useEffect(() => {
    if (selectedContentId) {
      setPreviewImageCacheTimestamp(Date.now())
      setPreviewBase64Image(null)
    }
  }, [selectedContentId])

  const { data: previewImageUrl, isLoading: isPreviewImageLoading } = useGetContentImageQuery(
    {
      ContentID: selectedContentId || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      _t: previewImageCacheTimestamp,
    },
    {
      skip: !selectedContentId || !selectedProjectId || !project?.datasetid,
    }
  )

  // Load preview image as base64
  useEffect(() => {
    if (!previewImageUrl) {
      setPreviewBase64Image(null)
      return
    }

    const loadImage = async () => {
      try {
        const response = await fetch(previewImageUrl)
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => {
          setPreviewBase64Image(reader.result as string)
        }
        reader.readAsDataURL(blob)
      } catch (error) {
        console.error('Error loading preview image:', error)
        setPreviewBase64Image(null)
      }
    }

    loadImage()
  }, [previewImageUrl])

  // moved to hook: allBoundingBoxes and getCurrentSelectedBox

  const baseBoxesForTab = useMemo(() => {
    if (activeRightTab === 'accepted') return acceptedBBoxes
    if (activeRightTab === 'rejected') return rejectedBBoxes
    return allUntouchedBBoxes
  }, [activeRightTab, acceptedBBoxes, rejectedBBoxes, allUntouchedBBoxes])

  // Derive focused index relative to ALL boxes (not just the current tab) since canvas shows all boxes
  const focusedIndexForTab = useMemo(() => {
    if (selectedBBoxClassIdx == null) return undefined
    const idx = allBoundingBoxes.findIndex((b) => b.class_index === selectedBBoxClassIdx)
    return idx >= 0 ? idx : undefined
  }, [allBoundingBoxes, selectedBBoxClassIdx])

  // If the current tab becomes empty, switch to the next tab that has items
  useEffect(() => {
    const counts: Record<PredictionTab, number> = {
      inspect: allUntouchedBBoxes.length,
      accepted: acceptedBBoxes.length,
      rejected: rejectedBBoxes.length,
    }
    if (counts[activeRightTab as PredictionTab] === 0) {
      const order: PredictionTab[] = ['inspect', 'accepted', 'rejected']
      const startIdx = order.indexOf(activeRightTab as PredictionTab)
      for (let i = 1; i <= 2; i++) {
        const nextTab = order[(startIdx + i) % order.length]
        if (counts[nextTab] > 0) {
          setActiveRightTab(nextTab)
          break
        }
      }
    }
  }, [
    activeRightTab,
    allUntouchedBBoxes.length,
    acceptedBBoxes.length,
    rejectedBBoxes.length,
    setActiveRightTab,
  ])

  // Build UI boxes from predictions via shared transform and seed Redux via hook
  const uiBoxes = useMemo(() => {
    if (!editingPrediction || !tags?.tags || !contentData) {
      return []
    }
    // Ensure contentData matches the current selectedContentId
    // This prevents using stale contentData from a previous image
    if (contentData.id !== selectedContentId) {
      return []
    }
    // Ensure editingPrediction matches the current content
    if (editingPrediction.contentid !== selectedContentId) {
      return []
    }
    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      const boxes = predictionToUIBoxes(
        [...editingPrediction.predictions].sort(
          (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
        ),
        tags.tags,
        contentData.width,
        contentData.height,
        project.annotation_type,
        0
      )
      return boxes
    }
    // Classification: synthesize UI items without geometry so right panel can render badges/buttons
    if (project?.annotation_type === 'classification') {
      const boxes = [...editingPrediction.predictions]
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
        .map((p, index) => {
          // For classification, try to find the tag ID by class name
          const tag = tags.tags.find((t) => t.name === p.class_name)
          return {
            name: tag?.id || p.tagid || p.class_name, // Store tag ID in name field for submission
            points: [], // no geometry for classification
            ai_labeled: true,
            confidence: p.confidence,
            class_index: index, // Use sequential index to ensure unique class_index for each prediction
            class_name: p.class_name,
            tagid: tag?.id || p.tagid, // Store tagid explicitly for classification
          }
        })
      return boxes as unknown as AnnotationDataBoundingBox[]
    }
    return []
  }, [
    editingPrediction,
    tags,
    contentData,
    project?.annotation_type,
    isContentDataLoading,
    selectedContentId,
  ])

  // Reset selection state when boxes change (moved out of useMemo to avoid render-phase updates)
  useEffect(() => {
    if (uiBoxes.length > 0 || editingPrediction) {
      setSelectedSnippetIndex(undefined)
      dispatch(setSelectedBoundingBox(undefined))
    }
  }, [editingPrediction?.id, dispatch, setSelectedSnippetIndex])

  // Reset image loaded state and clear boxes when contentId changes
  useEffect(() => {
    setIsImageLoaded(false)
    imageLoadedForContentIdRef.current = undefined
    // Clear boxes immediately when contentId changes to prevent stale annotations
    dispatch(clearAllBoundingBoxes())
  }, [selectedContentId, dispatch])

  // Initialize boxes when uiBoxes are available and match the current contentId
  // Don't wait for main image to load - boxes can be initialized independently
  const boxesToInitialize = useMemo(() => {
    if (!contentData || contentData.id !== selectedContentId) {
      return []
    }
    if (!editingPrediction || editingPrediction.contentid !== selectedContentId) {
      return []
    }
    if (uiBoxes.length === 0) {
      return []
    }
    return uiBoxes
  }, [contentData, selectedContentId, editingPrediction, uiBoxes])

  useInitializeBoxes({ mode: 'prediction', boxes: boxesToInitialize })

  // Handle image loaded callback
  const handleImageLoaded = useCallback(() => {
    // Only set loaded state if this is still the current contentId
    if (selectedContentId) {
      imageLoadedForContentIdRef.current = selectedContentId
      setIsImageLoaded(true)
    }
  }, [selectedContentId])

  // Handle modal image loaded callback
  const handleModalImageLoaded = useCallback(() => {
    setIsModalImageLoaded(true)
  }, [])

  const filteredPredictionsByConfidence = useMemo(() => {
    if (!allPredictions?.predictions) return []

    switch (activeConfidenceLevel) {
      case 'high':
        return allPredictions.predictions.filter((prediction) => {
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= confidenceRanges.high
        })
      case 'medium':
        return allPredictions.predictions.filter((prediction) => {
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= confidenceRanges.medium && maxConfidence < confidenceRanges.high
        })
      case 'low':
        return allPredictions.predictions.filter((prediction) => {
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence < confidenceRanges.medium && maxConfidence >= confidenceRanges.low
        })
      case 'none':
        return allPredictions.predictions.filter((prediction) => {
          return prediction.predictions.length === 0
        })
      default:
        return []
    }
  }, [allPredictions?.predictions, activeConfidenceLevel, confidenceRanges])

  const handleNextImage = useCallback(() => {
    if (!editingPrediction || !filteredPredictionsByConfidence.length) return
    const currentIndex = filteredPredictionsByConfidence.findIndex(
      (p) => p.id === editingPrediction.id
    )
    if (currentIndex > -1 && currentIndex < filteredPredictionsByConfidence.length - 1) {
      const nextPrediction = filteredPredictionsByConfidence[currentIndex + 1]

      // Reset all states before updating prediction
      resetForNextImage()

      // Update prediction data first
      dispatch(setEditingPrediction(nextPrediction))

      // Set selectedContentId immediately to ensure sync (useEffect will also sync, but this ensures it happens right away)
      if (nextPrediction.contentid) {
        dispatch(setSelectedContentId(nextPrediction.contentid))
      }
    } else {
      setShowNoMoreImagesModal(true)
    }
    setActiveRightTab('inspect')
  }, [
    editingPrediction,
    filteredPredictionsByConfidence,
    dispatch,
    resetForNextImage,
    setActiveRightTab,
  ])

  const handlePrevImage = useCallback(() => {
    if (!editingPrediction || !filteredPredictionsByConfidence.length) return
    const currentIndex = filteredPredictionsByConfidence.findIndex(
      (p) => p.id === editingPrediction.id
    )
    if (currentIndex > 0) {
      const prevPrediction = filteredPredictionsByConfidence[currentIndex - 1]

      // Reset all states before updating prediction
      resetForNextImage()

      // Update prediction data first
      dispatch(setEditingPrediction(prevPrediction))

      // Set selectedContentId immediately to ensure sync (useEffect will also sync, but this ensures it happens right away)
      if (prevPrediction.contentid) {
        dispatch(setSelectedContentId(prevPrediction.contentid))
      }
    }
    setActiveRightTab('inspect')
  }, [
    editingPrediction,
    filteredPredictionsByConfidence,
    dispatch,
    resetForNextImage,
    setActiveRightTab,
  ])

  // Centralized prediction keyboard shortcuts
  usePredictionShortcuts({
    isActive: !!editingPrediction && !isProcessing && !showSubmitModal && !showConfirmUnannotated,
    isResizing: () =>
      (annotationRef.current as ImageAnnotationComponentV2Handle | null)?.isResizing?.() ?? false,
    allBoxes: allBoundingBoxes,
    getCurrentSelectedBox,
    acceptBox: (box: AnnotationDataBoundingBox) => handleAcceptBoxKey(box),
    rejectBox: (box: AnnotationDataBoundingBox) => handleRejectBoxKey(box),
    onNextImage: handleNextImage,
    onPrevImage: handlePrevImage,
    onSubmitIfDone: () => {
      if (acceptedBBoxes.length > 0 || allUntouchedBBoxes.length > 0) {
        setShowSubmitModal(true)
      } else {
        setShowConfirmUnannotated(true)
      }
    },
  })

  const handleAcceptPredictions = useCallback(async () => {
    if (isProcessing) return
    setIsProcessing(true)
    if (editingPrediction) {
      try {
        const toSubmit = [...acceptedBBoxes, ...allUntouchedBBoxes]
        console.log('[handleAcceptPredictions] Classification submission:', {
          projectType: project?.annotation_type,
          toSubmit: toSubmit.map((b) => ({
            name: b.name,
            class_name: b.class_name,
            tagid: (b as any).tagid,
          })),
          acceptedCount: acceptedBBoxes.length,
          untouchedCount: allUntouchedBBoxes.length,
        })
        const annotationTagIds: string[] = Array.from(new Set(toSubmit.map((a) => a.name)))
        console.log('[handleAcceptPredictions] Tag IDs to submit:', annotationTagIds)
        const annoToCreate: CreateBBoxAnnotationReq = {
          content_id: editingPrediction.contentid || '',
          dataset_id: project?.datasetid || '',
          metadata:
            project?.annotation_type === 'bounding_box'
              ? { bounding_boxes: toSubmit }
              : project?.annotation_type === 'oriented_bounding_box'
              ? { oriented_bounding_boxes: toSubmit }
              : undefined,
          project_id: selectedProjectId || '',
          tag_id: annotationTagIds,
          model_id: selectedModelId || '',
          ai_labeled: toSubmit.some((b) => b.ai_labeled !== undefined && b.ai_labeled !== null),
          image_width: contentData?.width,
          image_height: contentData?.height,
        }
        console.log('[handleAcceptPredictions] Creating annotation:', annoToCreate)
        await createAnnotation(annoToCreate)
        console.log('[handleAcceptPredictions] Annotation created successfully!')
        // Refetch predictions to update the list
        dispatch(predictionApi.util.invalidateTags([apiTags.PredictionList, apiTags.Prediction]))
      } catch (error: unknown) {
        console.error('[handleAcceptPredictions] Error:', error)
        setShowError(true)
        const apiMessage = (error as { data?: { message?: string } } | undefined)?.data?.message
        const message = apiMessage || 'Unknown Error'
        setErrorMessage('Failed to create annotation: ' + message)
      }
      handleNextImage()
    } else {
      for (const prediction of selectedPredictions) {
        try {
          await createAnnotation({
            content_id: prediction.contentid,
            dataset_id: project?.datasetid || '',
            project_id: selectedProjectId || '',
            tag_id: prediction.predictions.map((p) => p.tagid),
            model_id: selectedModelId || '',
            ai_labeled: prediction.predictions.some(
              (p) => p.confidence !== undefined && p.confidence !== null && p.confidence > 0
            ),
            image_width: contentData?.width,
            image_height: contentData?.height,
            metadata:
              project?.annotation_type === 'bounding_box'
                ? {
                    bounding_boxes: prediction.predictions.flatMap((p) =>
                      p.bounding_boxes
                        ? Object.values(p.bounding_boxes)
                        : p.oriented_bounding_boxes
                        ? Object.values(p.oriented_bounding_boxes)
                        : []
                    ),
                  }
                : project?.annotation_type === 'oriented_bounding_box'
                ? {
                    oriented_bounding_boxes: prediction.predictions.flatMap((p) =>
                      p.oriented_bounding_boxes
                        ? Object.values(p.oriented_bounding_boxes)
                        : p.bounding_boxes
                        ? Object.values(p.bounding_boxes)
                        : []
                    ),
                  }
                : undefined,
          })
          dispatch(predictionApi.util.invalidateTags([apiTags.PredictionList, apiTags.Prediction]))
        } catch (error: unknown) {
          setShowError(true)
          const apiMessage = (error as { data?: { message?: string } } | null)?.data?.message
          const message = apiMessage || 'Unknown Error'
          setErrorMessage('Failed to create annotation: ' + message)
        }
      }
    }
    setIsProcessing(false)
  }, [
    editingPrediction,
    handleNextImage,
    acceptedBBoxes,
    allUntouchedBBoxes,
    project?.datasetid,
    project?.annotation_type,
    selectedProjectId,
    selectedModelId,
    contentData?.width,
    contentData?.height,
    createAnnotation,
    dispatch,
    selectedPredictions,
    isProcessing,
  ])

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel * 1.2, 5)
    setZoomLevel(newZoom)
    const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
    if (activeRef) {
      activeRef.setZoom(newZoom)
    }
  }

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel / 1.2, 0.2)
    setZoomLevel(newZoom)
    const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
    if (activeRef) {
      activeRef.setZoom(newZoom)
    }
  }

  const handleResetZoom = () => {
    const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
    activeRef?.recenterImage?.()
    handleResetContrast()
  }

  const handleUndo = () => {
    const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
    activeRef?.undo()
  }

  const handleRedo = () => {
    const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
    activeRef?.redo()
  }

  const handleDelete = () => {
    const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
    activeRef?.delete()
  }

  const handleContrastChange = useCallback(
    (newContrast: number) => {
      setContrast(newContrast)
      const activeRef = showImageEditModal ? modalAnnotationRef.current : annotationRef.current
      activeRef?.setContrast(newContrast)
    },
    [showImageEditModal]
  )

  const handleResetContrast = useCallback(() => {
    handleContrastChange(0)
  }, [handleContrastChange])

  // Sync contrast from ref when modal opens or content changes
  useEffect(() => {
    if (showImageEditModal && modalAnnotationRef.current) {
      const currentContrast = modalAnnotationRef.current.getContrast()
      setContrast(currentContrast)
    }
  }, [showImageEditModal, selectedContentId])

  // const handleDrawBox = () => {
  //   setIsDrawing(!isDrawing)
  // }

  // Updated accept and reject handlers using the new setBoundingBoxStatus action
  const handleAcceptBoxKey = useCallback(
    (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => {
      e?.stopPropagation()
      dispatch(saveStateToHistory())

      // Set the box status to accepted first
      // (We'll select and zoom after status is updated)
      dispatch(
        setBoundingBoxStatus({
          classIndex: box.class_index,
          status: BBoxStatus.Accepted,
        })
      )
      // Only advance selection if this action moves the box out of the current list
      if (activeRightTab !== 'accepted') {
        const currentList = activeRightTab === 'rejected' ? rejectedBBoxes : allUntouchedBBoxes
        const currentIndex = currentList.findIndex((b) => b.class_index === box.class_index)
        if (currentIndex !== -1) {
          const newListLen = currentList.length - 1
          if (newListLen > 0) {
            // Select neighbor within the same list (positional in current tab)
            const newList = currentList.filter((b) => b.class_index !== box.class_index)
            const targetIndex = Math.min(currentIndex, newList.length - 1)
            const nextBox = newList[targetIndex]
            dispatch(setSelectedBoundingBox(nextBox))
            setSelectedSnippetIndex(targetIndex)
            // Zoom to next box after state updates
            const targetRef = showImageEditModal
              ? modalAnnotationRef.current
              : annotationRef.current
            setTimeout(() => {
              targetRef?.zoomToBBox(nextBox)
            }, 50)
          } else {
            // List became empty; switch to the next tab over that has items
            dispatch(setSelectedBoundingBox(undefined))
            setSelectedSnippetIndex(undefined)
            const tabOrder: PredictionTab[] = ['inspect', 'accepted', 'rejected']
            const currentIdx = tabOrder.indexOf(activeRightTab)
            // Calculate lengths after this accept action
            const inspectLenAfter =
              allUntouchedBBoxes.length - (activeRightTab === 'inspect' ? 1 : 0)
            const acceptedLenAfter = acceptedBBoxes.length + 1
            const rejectedLenAfter = rejectedBBoxes.length - (activeRightTab === 'rejected' ? 1 : 0)
            const lenByTab: Record<PredictionTab, number> = {
              inspect: inspectLenAfter,
              accepted: acceptedLenAfter,
              rejected: rejectedLenAfter,
            }
            for (let k = 1; k <= 2; k++) {
              const nextTab = tabOrder[(currentIdx + k) % tabOrder.length]
              if (lenByTab[nextTab] > 0) {
                setActiveRightTab(nextTab)
                break
              }
            }
          }
        }
      }
    },
    [
      dispatch,
      activeRightTab,
      acceptedBBoxes,
      rejectedBBoxes,
      allUntouchedBBoxes,
      setSelectedSnippetIndex,
      setActiveRightTab,
      showImageEditModal,
    ]
  )

  const handleRejectBoxKey = useCallback(
    (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => {
      e?.stopPropagation()
      dispatch(saveStateToHistory())

      // Set the box status to rejected first
      // (We'll select after status is updated)
      dispatch(
        setBoundingBoxStatus({
          classIndex: box.class_index,
          status: BBoxStatus.Rejected,
        })
      )
      // Only advance selection if this action moves the box out of the current list
      if (activeRightTab !== 'rejected') {
        const currentList = activeRightTab === 'accepted' ? acceptedBBoxes : allUntouchedBBoxes
        const currentIndex = currentList.findIndex((b) => b.class_index === box.class_index)
        if (currentIndex !== -1) {
          const newListLen = currentList.length - 1
          if (newListLen > 0) {
            // Select neighbor within the same list (positional in current tab)
            const newList = currentList.filter((b) => b.class_index !== box.class_index)
            const targetIndex = Math.min(currentIndex, newList.length - 1)
            const nextBox = newList[targetIndex]
            dispatch(setSelectedBoundingBox(nextBox))
            setSelectedSnippetIndex(targetIndex)
            // Zoom to next box after state updates
            const targetRef = showImageEditModal
              ? modalAnnotationRef.current
              : annotationRef.current
            setTimeout(() => {
              targetRef?.zoomToBBox(nextBox)
            }, 50)
          } else {
            // List became empty; switch to the next tab over that has items
            dispatch(setSelectedBoundingBox(undefined))
            setSelectedSnippetIndex(undefined)
            const tabOrder: PredictionTab[] = ['inspect', 'accepted', 'rejected']
            const currentIdx = tabOrder.indexOf(activeRightTab)
            // Calculate lengths after this reject action
            const inspectLenAfter =
              allUntouchedBBoxes.length - (activeRightTab === 'inspect' ? 1 : 0)
            const acceptedLenAfter = acceptedBBoxes.length - (activeRightTab === 'accepted' ? 1 : 0)
            const rejectedLenAfter = rejectedBBoxes.length + 1
            const lenByTab: Record<PredictionTab, number> = {
              inspect: inspectLenAfter,
              accepted: acceptedLenAfter,
              rejected: rejectedLenAfter,
            }
            for (let k = 1; k <= 2; k++) {
              const nextTab = tabOrder[(currentIdx + k) % tabOrder.length]
              if (lenByTab[nextTab] > 0) {
                setActiveRightTab(nextTab)
                break
              }
            }
          }
        }
      }
    },
    [
      dispatch,
      activeRightTab,
      acceptedBBoxes,
      rejectedBBoxes,
      allUntouchedBBoxes,
      setSelectedSnippetIndex,
      setActiveRightTab,
      showImageEditModal,
    ]
  )

  // AAL Review Modal: Keyboard shortcuts for Accept/Reject/Inspect
  useEffect(() => {
    if (!showImageEditModal) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input fields
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
      ) {
        return
      }

      const currentBox = getCurrentSelectedBox()
      if (!currentBox || currentBox.class_index === undefined) return

      if (e.key === 'a' || e.key === 'A') {
        // Accept selected box (using proper handler)
        e.preventDefault()
        handleAcceptBoxKey(currentBox)
      } else if (e.key === 'r' || e.key === 'R') {
        // Reject selected box (using proper handler)
        e.preventDefault()
        handleRejectBoxKey(currentBox)
      } else if (e.key === 'i' || e.key === 'I') {
        // Mark as needs inspection (untouched)
        e.preventDefault()
        if (currentBox.class_index !== undefined) {
          dispatch(
            setBoundingBoxStatus({
              classIndex: currentBox.class_index,
              status: BBoxStatus.Untouched,
            })
          )
          dispatch(saveStateToHistory())
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showImageEditModal, dispatch, getCurrentSelectedBox, handleAcceptBoxKey, handleRejectBoxKey])

  // Bulk accept all items in the current tab
  const handleBulkAccept = useCallback(() => {
    dispatch(saveStateToHistory())

    const itemsToAccept = activeRightTab === 'rejected' ? rejectedBBoxes : allUntouchedBBoxes

    itemsToAccept.forEach((box) => {
      dispatch(
        setBoundingBoxStatus({
          classIndex: box.class_index,
          status: BBoxStatus.Accepted,
        })
      )
    })

    // Clear selection and switch to accepted tab after bulk action
    dispatch(setSelectedBoundingBox(undefined))
    setSelectedSnippetIndex(undefined)
    setActiveRightTab('accepted')
  }, [
    dispatch,
    activeRightTab,
    rejectedBBoxes,
    allUntouchedBBoxes,
    setSelectedSnippetIndex,
    setActiveRightTab,
  ])

  // Bulk reject all items in the current tab
  const handleBulkReject = useCallback(() => {
    dispatch(saveStateToHistory())

    const itemsToReject = activeRightTab === 'accepted' ? acceptedBBoxes : allUntouchedBBoxes

    itemsToReject.forEach((box) => {
      dispatch(
        setBoundingBoxStatus({
          classIndex: box.class_index,
          status: BBoxStatus.Rejected,
        })
      )
    })

    // Clear selection and switch to rejected tab after bulk action
    dispatch(setSelectedBoundingBox(undefined))
    setSelectedSnippetIndex(undefined)
    setActiveRightTab('rejected')
  }, [
    dispatch,
    activeRightTab,
    acceptedBBoxes,
    allUntouchedBBoxes,
    setSelectedSnippetIndex,
    setActiveRightTab,
  ])

  const handleSelectTab = (key: string | null) => {
    const nextTab: PredictionTab =
      key === 'accepted' ? 'accepted' : key === 'rejected' ? 'rejected' : 'inspect'
    setActiveRightTab(nextTab)
    dispatch(setSelectedBoundingBox(undefined))
    setSelectedSnippetIndex(undefined)
    annotationRef.current?.recenterImage?.()
  }

  // Handler for clicking a snippet in the Inspect tab
  const handleSnippetClick = (box: AnnotationDataBoundingBox) => {
    const idx = allBoundingBoxes.findIndex((b) => b.class_index === box.class_index)
    setSelectedSnippetIndex(idx >= 0 ? idx : undefined)

    // First, set the selected box in Redux (this will trigger the Canvas to highlight it)
    dispatch(setSelectedBoundingBox(box))

    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      if (showImageEditModal) {
        // Modal is already open, zoom to the box immediately
        // Add a small delay to ensure the box selection is rendered first
        setTimeout(() => {
          modalAnnotationRef.current?.zoomToBBox(box)
        }, 50)
      } else {
        // Modal is not open, open it and store the box to auto-select
        setBoxToAutoSelect(box)
        setShowImageEditModal(true)
      }
    }
  }

  const handleBackToGallery = () => {
    dispatch(clearEditingPrediction())
  }

  // Auto-select and zoom to box when modal opens with a box to select
  // Wait for both the modal to be open AND the image to be loaded
  useEffect(() => {
    if (showImageEditModal && boxToAutoSelect && isModalImageLoaded) {
      // Wait a small amount for the canvas to be fully ready
      const timer = setTimeout(() => {
        if (modalAnnotationRef.current && modalAnnotationRef.current.zoomToBBox) {
          modalAnnotationRef.current.zoomToBBox(boxToAutoSelect)
          setBoxToAutoSelect(null)
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [showImageEditModal, boxToAutoSelect, isModalImageLoaded])

  // Clear boxToAutoSelect and reset image loaded state when modal closes
  useEffect(() => {
    if (!showImageEditModal) {
      setBoxToAutoSelect(null)
      setIsModalImageLoaded(false)
    }
  }, [showImageEditModal])

  return (
    <>
      <div
        className="flex-grow-1 d-flex flex-column"
        style={{ minHeight: 0, height: '65vh', maxHeight: '65vh', overflow: 'hidden' }}
      >
        {/* Navigation Toolbar */}
        <div
          className="d-flex align-items-center justify-content-between w-100 px-3 flex-shrink-0"
          style={{
            background: 'rgba(0,0,0,0.8)',
            padding: '8px 12px',
            borderRadius: '4px',
            height: '48px',
            minHeight: '48px',
            marginBottom: '8px',
          }}
        >
          {/* Left: Navigation */}
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleBackToGallery}
              aria-label="Back to images"
              title="Back to images"
              className="d-flex align-items-center gap-2"
              style={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#fff',
                padding: '6px 12px',
                fontWeight: 500,
              }}
            >
              <ArrowLeft size={16} />
              <span>Back to Images</span>
            </Button>
            <div
              style={{
                width: '1px',
                height: '24px',
                background: 'rgba(255, 255, 255, 0.2)',
                margin: '0 4px',
              }}
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handlePrevImage}
              aria-label="Previous image"
              title="Previous image"
              style={{ border: 'none', background: 'transparent' }}
              disabled={!editingPrediction || filteredPredictionsByConfidence.length === 0}
            >
              <ChevronLeft size={18} color="#fff" />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleNextImage}
              aria-label="Next image"
              title="Next image"
              style={{ border: 'none', background: 'transparent' }}
              disabled={!editingPrediction || filteredPredictionsByConfidence.length === 0}
            >
              <ChevronRight size={18} color="#fff" />
            </Button>
          </div>
        </div>

        <div className="flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
          <ReviewScaffold
            left={null}
            center={
              selectedContentId ? (
                <PredictionRightPanel
                  contentId={selectedContentId}
                  activeTab={activeRightTab}
                  onSelectTab={handleSelectTab}
                  untouched={allUntouchedBBoxes}
                  accepted={acceptedBBoxes}
                  rejected={rejectedBBoxes}
                  selectedBBoxClassIndex={selectedBBoxClassIdx ?? undefined}
                  onSnippetSelect={handleSnippetClick}
                  onAccept={handleAcceptBoxKey}
                  onReject={handleRejectBoxKey}
                  onBulkAccept={handleBulkAccept}
                  onBulkReject={handleBulkReject}
                  hoveredBoxIndex={hoveredBoxIndex}
                  onHover={setHoveredBoxIndex}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center h-100"
                  style={{ backgroundColor: '#161b22' }}
                >
                  <div className="text-center text-muted">
                    <p className="mb-0">No image selected</p>
                    <p className="small mt-2">Click an image from the gallery to review</p>
                  </div>
                </div>
              )
            }
            right={
              selectedContentId ? (
                <div className="d-flex flex-column h-100" style={{ backgroundColor: '#0d1117' }}>
                  {/* Image Preview - Top Section */}
                  <div
                    className="position-relative flex-shrink-0"
                    style={{
                      height: '45%',
                      minHeight: '200px',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                    onClick={() => setShowImageEditModal(true)}
                    title="Click to open full-size editor"
                  >
                    {isProcessing && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50"
                        style={{ zIndex: 10 }}
                      >
                        <div className="text-center">
                          <Spinner animation="border" variant="light" />
                          <div className="mt-2 text-light">Submitting annotations…</div>
                        </div>
                      </div>
                    )}
                    <div className="w-100 h-100" style={{ backgroundColor: '#0d1117' }}>
                      {previewBase64Image && contentData ? (
                        <ContentImageCard
                          base64Data={previewBase64Image}
                          bounding_boxes={uiBoxes}
                          contentWidth={contentData.width}
                          contentHeight={contentData.height}
                          showConfidence={false}
                          showBoundingBoxes={
                            (project?.annotation_type === 'bounding_box' ||
                              project?.annotation_type === 'oriented_bounding_box') &&
                            showBBoxes &&
                            uiBoxes.length > 0
                          }
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                          {isPreviewImageLoading ? (
                            <Spinner animation="border" variant="light" size="sm" />
                          ) : (
                            <div className="text-center text-muted">
                              <p className="mb-0 small">No image available</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Click to Edit Overlay */}
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{
                        zIndex: 5,
                        background: 'rgba(0,0,0,0.3)',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0'
                      }}
                    >
                      <div
                        className="text-light text-center px-3 py-2 rounded"
                        style={{
                          background: 'rgba(0,0,0,0.8)',
                          fontSize: '0.875rem',
                        }}
                      >
                        Click to edit image
                      </div>
                    </div>
                  </div>

                  {/* Submit Button Section */}
                  <div
                    className="flex-shrink-0 p-3 border-top border-bottom"
                    style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    {rejectedBBoxes.length > 0 && (
                      <div
                        className="alert alert-warning py-2 px-2 mb-2 d-flex align-items-start gap-2"
                        style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}
                      >
                        <AlertTriangle size={14} className="flex-shrink-0 mt-1" />
                        <div>
                          <strong>
                            {rejectedBBoxes.length} rejection
                            {rejectedBBoxes.length !== 1 ? 's' : ''}
                          </strong>
                          <div className="mt-1">Consider fixing these to improve your model</div>
                        </div>
                      </div>
                    )}
                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={() => {
                        // Check if there are rejections - show educational modal
                        if (rejectedBBoxes.length > 0) {
                          setShowFixRejectionsModal(true)
                        } else if (acceptedBBoxes.length > 0 || allUntouchedBBoxes.length > 0) {
                          setShowSubmitModal(true)
                        } else {
                          setShowConfirmUnannotated(true)
                        }
                      }}
                      disabled={
                        isProcessing ||
                        (acceptedBBoxes.length === 0 &&
                          allUntouchedBBoxes.length === 0 &&
                          rejectedBBoxes.length === 0)
                      }
                      style={{
                        fontWeight: 600,
                        padding: '0.5rem 1rem',
                      }}
                    >
                      {isProcessing ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Annotations'
                      )}
                    </Button>
                  </div>

                  {/* Stats Section - Bottom */}
                  <div
                    className="flex-grow-1 d-flex flex-column p-3"
                    style={{ overflow: 'auto', minHeight: 0 }}
                  >
                    <h6
                      className="text-light mb-2"
                      style={{ fontSize: '0.875rem', fontWeight: 600 }}
                    >
                      Image Statistics
                    </h6>
                    <p
                      className="mb-3"
                      style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      Annotations for this image
                    </p>

                    <div className="d-flex flex-column gap-3">
                      {/* Total Annotations */}
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span
                            style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            Total Annotations
                          </span>
                          <span className="text-light fw-semibold">{allBoundingBoxes.length}</span>
                        </div>
                      </div>

                      {/* Inspect Count */}
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span
                            className="d-flex align-items-center gap-1"
                            style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            <Eye size={14} />
                            To Review
                          </span>
                          <span className="text-warning fw-semibold">
                            {allUntouchedBBoxes.length}
                          </span>
                        </div>
                        {allBoundingBoxes.length > 0 && (
                          <div
                            className="progress"
                            style={{ height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          >
                            <div
                              className="progress-bar bg-warning"
                              role="progressbar"
                              style={{
                                width: `${
                                  (allUntouchedBBoxes.length / allBoundingBoxes.length) * 100
                                }%`,
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Accepted Count */}
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span
                            className="d-flex align-items-center gap-1"
                            style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            <CheckCircle2 size={14} />
                            Accepted
                          </span>
                          <span className="text-success fw-semibold">{acceptedBBoxes.length}</span>
                        </div>
                        {allBoundingBoxes.length > 0 && (
                          <div
                            className="progress"
                            style={{ height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          >
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{
                                width: `${
                                  (acceptedBBoxes.length / allBoundingBoxes.length) * 100
                                }%`,
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Rejected Count */}
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span
                            className="d-flex align-items-center gap-1"
                            style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            <XCircle size={14} />
                            Rejected
                          </span>
                          <span className="text-danger fw-semibold">{rejectedBBoxes.length}</span>
                        </div>
                        {allBoundingBoxes.length > 0 && (
                          <div
                            className="progress"
                            style={{ height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          >
                            <div
                              className="progress-bar bg-danger"
                              role="progressbar"
                              style={{
                                width: `${
                                  (rejectedBBoxes.length / allBoundingBoxes.length) * 100
                                }%`,
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Progress Summary */}
                      {allBoundingBoxes.length > 0 && (
                        <div className="mt-3 pt-3 border-top border-secondary">
                          <div className="d-flex justify-content-between align-items-center">
                            <span
                              style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}
                            >
                              Review Progress
                            </span>
                            <span className="text-light fw-semibold">
                              {Math.round(
                                ((acceptedBBoxes.length + rejectedBBoxes.length) /
                                  allBoundingBoxes.length) *
                                  100
                              )}
                              %
                            </span>
                          </div>
                          <div
                            className="progress mt-2"
                            style={{ height: '6px', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          >
                            <div
                              className="progress-bar bg-primary"
                              role="progressbar"
                              style={{
                                width: `${
                                  ((acceptedBBoxes.length + rejectedBBoxes.length) /
                                    allBoundingBoxes.length) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center h-100"
                  style={{ backgroundColor: '#0d1117' }}
                >
                  <div className="text-center text-muted">
                    <p className="mb-0">No image selected</p>
                  </div>
                </div>
              )
            }
          />
        </div>
      </div>
      <SubmitPredictionsModal
        show={showSubmitModal}
        onHide={() => setShowSubmitModal(false)}
        onConfirm={() => {
          handleAcceptPredictions()
          setShowSubmitModal(false)
        }}
        inspectCount={allUntouchedBBoxes.length}
        acceptedCount={acceptedBBoxes.length}
        rejectedCount={rejectedBBoxes.length}
      />
      <FixRejectionsInfoModal
        show={showFixRejectionsModal}
        onHide={() => setShowFixRejectionsModal(false)}
        onContinueAnyway={() => {
          setShowFixRejectionsModal(false)
          setShowSubmitModal(true)
        }}
        onGoBack={() => {
          setShowFixRejectionsModal(false)
          // Open the full image editing modal to allow corrections
          setShowImageEditModal(true)
        }}
        rejectedCount={rejectedBBoxes.length}
      />
      <Modal
        show={showNoMoreImagesModal}
        onHide={() => {
          setShowNoMoreImagesModal(false)
          handleBackToGallery()
        }}
        centered
      >
        <Modal.Header closeButton closeVariant="white" className="bg-dark text-white border-0">
          <Modal.Title className="d-flex align-items-center gap-2">
            <CheckCircle2 size={24} className="text-success" />
            Review Complete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-white">
          <div className="d-flex flex-column gap-3">
            <p className="mb-0">
              🎉 Great work! You've reviewed all available images in this batch.
            </p>
            <div
              className="p-3 rounded"
              style={{
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
              }}
            >
              <small className="text-light">
                <strong>What's Next:</strong>
                <ul className="mb-0 mt-2 ps-3">
                  <li>Your annotations have been saved and will be used for training</li>
                  <li>Return to the image gallery to review more images if needed</li>
                  <li>Or proceed to fine-tune your model with the updated data</li>
                </ul>
              </small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-0">
          <Button
            variant="primary"
            onClick={() => {
              setShowNoMoreImagesModal(false)
              handleBackToGallery()
            }}
          >
            Back to Gallery
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog
        show={showConfirmUnannotated}
        onHide={() => setShowConfirmUnannotated(false)}
        onConfirmRight={() => handleAcceptPredictions()}
        title={'Create a Null Annotation'}
        message={
          'Proceeding will make this image a null annotation. To keep this image in Content as an unannotated image cancel and select the next image.'
        }
        variant="primary"
        confirmRightText={'Make Null'}
        cancelText="Cancel"
      />
      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Prediction Review Error"
      />

      {/* Full-Size Image Editing Modal */}
      <Modal
        show={showImageEditModal}
        onHide={() => {
          setShowImageEditModal(false)
          setBoxToAutoSelect(null)
          setIsModalImageLoaded(false)
        }}
        size="xl"
        centered
        fullscreen="lg-down"
        dialogClassName="modal-90w"
        style={{ maxWidth: 'none' }}
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="bg-dark text-white border-0"
          style={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Modal.Title>Edit Image Annotations</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="p-0 bg-dark"
          style={{
            height: '80vh',
            minHeight: '600px',
          }}
        >
          {selectedContentId && (
            <Container fluid className="h-100 p-0">
              <Row className="g-0 h-100 m-0">
                {/* Keyboard Shortcuts Panel - Conditional Left Column */}
                {showKeyboardShortcutsPanel && (
                  <Col
                    xs={12}
                    lg={2}
                    className="d-flex flex-column p-0"
                    style={{
                      minHeight: 0,
                      maxHeight: '100%',
                      height: '100%',
                      borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <KeyboardShortcutsPanel
                      onClose={() => setShowKeyboardShortcutsPanel(false)}
                      mode="review"
                    />
                  </Col>
                )}
                {/* Image Column - Center */}
                <Col
                  xs={12}
                  lg={showKeyboardShortcutsPanel ? 6 : 8}
                  className="p-0 d-flex"
                  style={{ height: '100%', maxHeight: '100%', minHeight: 0 }}
                >
                  <div className="position-relative w-100 h-100" style={{ background: '#0d1117' }}>
                    <ImageAnnotationComponentV2
                      ref={modalAnnotationRef}
                      contentId={selectedContentId}
                      useBBoxes={
                        project?.annotation_type === 'bounding_box' ||
                        project?.annotation_type === 'oriented_bounding_box'
                      }
                      focusedBBoxIndex={focusedIndexForTab}
                      showBBoxes={showBBoxes}
                      filteredBoxes={filteredBoundingBoxes}
                      zoomLevel={zoomLevel}
                      onZoomChange={setZoomLevel}
                      hoveredBoxIndex={hoveredBoxIndex}
                      showConfidence={true}
                      labelMode={'class+confidence'}
                      isEditable={
                        project?.annotation_type === 'bounding_box' ||
                        project?.annotation_type === 'oriented_bounding_box'
                      }
                      selectOnFocus={true}
                      onImageLoaded={handleModalImageLoaded}
                      bboxStatuses={bboxStatuses}
                      contentWidth={contentData?.width}
                      contentHeight={contentData?.height}
                    />
                    {/* Vertical Contrast Control - Left Side */}
                    <div
                      className="position-absolute d-flex flex-column align-items-center"
                      style={{
                        top: '50%',
                        left: '8px',
                        transform: 'translateY(-50%)',
                        zIndex: 11,
                        background: 'rgba(0,0,0,0.8)',
                        padding: '12px 0',
                        borderRadius: '12px',
                        gap: '12px',
                        width: '24px',
                        minHeight: '140px',
                      }}
                    >
                      <FaSun
                        onClick={handleResetContrast}
                        style={{
                          cursor: 'pointer',
                          color: '#fff',
                          fontSize: '16px',
                          flexShrink: 0,
                        }}
                        title="Reset contrast"
                      />
                      <div
                        style={{
                          position: 'relative',
                          width: '120px',
                          height: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'visible',
                        }}
                      >
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          value={contrast}
                          onChange={(e) => handleContrastChange(Number(e.target.value))}
                          className="form-range"
                          style={{
                            width: '120px',
                            height: '6px',
                            transform: 'rotate(-90deg)',
                            transformOrigin: 'center',
                            position: 'absolute',
                            margin: 0,
                            padding: 0,
                          }}
                          title="Adjust image contrast"
                        />
                      </div>
                    </div>
                    {/* Full Controls Toolbar */}
                    <div
                      className="position-absolute d-flex align-items-center w-100"
                      style={{ top: '12px', left: 0, right: 0, zIndex: 11, padding: '0 12px' }}
                    >
                      {/* Center: Annotation Tools */}
                      <div
                        className="d-flex align-items-center gap-2 mx-auto"
                        style={{
                          background: 'rgba(0,0,0,0.8)',
                          padding: '8px 12px',
                          borderRadius: '20px',
                        }}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleUndo}
                          aria-label="Undo"
                          title="Undo"
                          style={{ border: 'none', background: 'transparent' }}
                        >
                          <Undo size={16} color="#fff" />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleRedo}
                          aria-label="Redo"
                          title="Redo"
                          style={{ border: 'none', background: 'transparent' }}
                        >
                          <Redo size={16} color="#fff" />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleDelete}
                          aria-label="Delete"
                          title="Delete"
                          style={{ border: 'none', background: 'transparent' }}
                        >
                          <Trash2 size={16} color="#fff" />
                        </Button>
                        <div
                          style={{
                            width: '1px',
                            height: '20px',
                            background: 'rgba(255,255,255,0.2)',
                          }}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowKeyboardShortcutsPanel(!showKeyboardShortcutsPanel)}
                          aria-label="Keyboard Shortcuts"
                          title="Keyboard Shortcuts (? key)"
                          style={{
                            border: 'none',
                            background: showKeyboardShortcutsPanel
                              ? 'rgba(13, 110, 253, 0.9)'
                              : 'transparent',
                          }}
                        >
                          <Keyboard size={16} color="#fff" />
                        </Button>
                      </div>

                      {/* Right: View Controls */}
                      <div
                        className="d-flex align-items-center gap-2 ms-auto"
                        style={{
                          background: 'rgba(0,0,0,0.8)',
                          padding: '8px 12px',
                          borderRadius: '20px',
                        }}
                      >
                        <Button
                          variant={showBBoxes ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setShowBBoxes(!showBBoxes)}
                          aria-label={showBBoxes ? 'Hide boxes' : 'Show boxes'}
                          title={showBBoxes ? 'Hide boxes' : 'Show boxes'}
                          style={{
                            border: 'none',
                            background: showBBoxes ? 'rgba(13, 110, 253, 0.9)' : 'transparent',
                          }}
                        >
                          {showBBoxes ? (
                            <EyeOff size={16} color="#fff" />
                          ) : (
                            <Eye size={16} color="#fff" />
                          )}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleZoomIn}
                          aria-label="Zoom in"
                          title="Zoom in"
                          style={{ border: 'none', background: 'transparent' }}
                        >
                          <ZoomIn size={16} color="#fff" />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleZoomOut}
                          aria-label="Zoom out"
                          title="Zoom out"
                          style={{ border: 'none', background: 'transparent' }}
                        >
                          <ZoomOut size={16} color="#fff" />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleResetZoom}
                          aria-label="Reset zoom"
                          title="Reset zoom"
                          style={{ border: 'none', background: 'transparent' }}
                        >
                          <RotateCcw size={16} color="#fff" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
                {/* Snippets Column - Right Side */}
                <Col
                  xs={12}
                  lg={4}
                  className="d-flex flex-column p-0"
                  style={{
                    height: '100%',
                    maxHeight: '100%',
                    background: '#161b22',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div
                    className="h-100 d-flex flex-column overflow-hidden"
                    style={{ minHeight: 0 }}
                  >
                    <div
                      className="px-3 py-3 border-bottom"
                      style={{
                        borderColor: 'rgba(255, 255, 255, 0.1) !important',
                        background: '#0d1117',
                      }}
                    >
                      <h6 className="text-light mb-0 fw-bold">
                        {project?.annotation_type === 'classification' ? 'Predictions' : 'Snippets'}
                      </h6>
                    </div>
                    <div className="flex-grow-1 overflow-auto px-2 py-2">
                      {project?.annotation_type === 'classification' ? (
                        <ClassificationReviewGrid
                          items={allBoundingBoxes}
                          selectedBBoxClassIndex={selectedBBoxClassIdx ?? undefined}
                          showAcceptButton={true}
                          showRejectButton={true}
                          onAccept={handleAcceptBoxKey}
                          onReject={handleRejectBoxKey}
                          onSelect={handleSnippetClick}
                        />
                      ) : (
                        <SnippetList
                          contentId={selectedContentId}
                          items={allBoundingBoxes}
                          selectedBBoxClassIndex={selectedBBoxClassIdx ?? undefined}
                          showConfidence={true}
                          showAcceptButton={true}
                          showRejectButton={true}
                          showInspectButton={false}
                          onAccept={handleAcceptBoxKey}
                          onReject={handleRejectBoxKey}
                          hoveredBoxIndex={hoveredBoxIndex}
                          onHover={setHoveredBoxIndex}
                          onSelect={handleSnippetClick}
                          statusFilter={statusFilter}
                          onStatusFilterChange={setStatusFilter}
                          rotateLongSideVertical={
                            project?.annotation_type === 'oriented_bounding_box' && false
                          }
                        />
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default PredictionReview
